import InlineWorker from 'inline-worker';

class Recorder {
  static workletModuleLoaded = new WeakMap();

  constructor(source, cfg) {
    this.config = {
      bufferLen: 4096,
      numChannels: 2,
      mimeType: 'audio/wav',
    };
    this.recording = false;
    this.callbacks = {
      getBuffer: [],
      exportWAV: [],
    };

    Object.assign(this.config, cfg);
    this.context = source.context;

    // Helper to set up the original ScriptProcessorNode (fallback)
    const setupScriptProcessor = () => {
      this.node = (this.context.createScriptProcessor || this.context.createJavaScriptNode).call(
        this.context,
        this.config.bufferLen,
        this.config.numChannels,
        this.config.numChannels
      );

      this.node.onaudioprocess = (e) => {
        if (!this.recording) return;

        const buffer = [];
        for (let channel = 0; channel < this.config.numChannels; channel++) {
          buffer.push(e.inputBuffer.getChannelData(channel));
        }
        this.worker.postMessage({
          command: 'record',
          buffer,
        });
      };

      source.connect(this.node);
      // connecting to destination is not necessary but kept for compatibility
      try {
        this.node.connect(this.context.destination);
      } catch (err) {
        // ignore if not allowed
      }
    };

    // Prefer AudioWorkletNode; fall back to ScriptProcessor
    if (this.context.audioWorklet && typeof AudioWorkletNode === 'function') {
      // Only add module once per context
      if (!Recorder.workletModuleLoaded.get(this.context)) {
        const workletSource = `
          class RecorderProcessor extends AudioWorkletProcessor {
            process(inputs) {
              const input = inputs[0];
              if (!input || input.length === 0) return true;
              const channels = [];
              for (let ch = 0; ch < input.length; ch++) {
                channels.push(input[ch] ? input[ch].slice(0) : new Float32Array(128));
              }
              this.port.postMessage({ command: 'record', buffer: channels });
              return true;
            }
          }
          registerProcessor('recorder-processor', RecorderProcessor);
        `;
        const blob = new Blob([workletSource], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);

        this.context.audioWorklet.addModule(url).then(() => {
          Recorder.workletModuleLoaded.set(this.context, true);
          this.node = new AudioWorkletNode(this.context, 'recorder-processor', {
            numberOfInputs: 1,
            numberOfOutputs: 0,
            channelCount: this.config.numChannels,
          });
          this.node.port.onmessage = (e) => {
            if (!this.recording) return;
            const data = e.data;
            if (data && data.command === 'record') {
              this.worker.postMessage({ command: 'record', buffer: data.buffer });
            }
          };
          source.connect(this.node);
        }).catch((err) => {
          console.warn('AudioWorklet failed to load � falling back to ScriptProcessor:', err);
          setupScriptProcessor();
        }).finally(() => {
          try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
        });
      } else {
        // Already loaded, just create the node
        this.node = new AudioWorkletNode(this.context, 'recorder-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          channelCount: this.config.numChannels,
        });
        this.node.port.onmessage = (e) => {
          if (!this.recording) return;
          const data = e.data;
          if (data && data.command === 'record') {
            this.worker.postMessage({ command: 'record', buffer: data.buffer });
          }
        };
        source.connect(this.node);
      }
    } else {
      // no AudioWorklet support
      setupScriptProcessor();
    }

    this.worker = new InlineWorker(function () {
      let recLength = 0;
      let recBuffers = [];
      let sampleRate;
      let numChannels;

      // eslint-disable-next-line no-restricted-globals
      self.onmessage = (e) => {
        switch (e.data.command) {
          case 'init':
            init(e.data.config);
            break;
          case 'record':
            record(e.data.buffer);
            break;
          case 'exportWAV':
            exportWAV(e.data.type);
            break;
          case 'getBuffer':
            getBuffer();
            break;
          case 'clear':
            clear();
            break;
          default:
            break;
        }
      };

      function init(config) {
        sampleRate = config.sampleRate;
        numChannels = config.numChannels;
        initBuffers();
      }

      function record(inputBuffer) {
        for (let channel = 0; channel < numChannels; channel++) {
          recBuffers[channel].push(inputBuffer[channel]);
        }
        recLength += inputBuffer[0].length;
      }

      function exportWAV(type) {
        const buffers = [];
        for (let channel = 0; channel < numChannels; channel++) {
          buffers.push(mergeBuffers(recBuffers[channel], recLength));
        }
        const interleaved = numChannels === 2 ? interleave(buffers[0], buffers[1]) : buffers[0];
        const dataview = encodeWAV(interleaved);
        const audioBlob = new Blob([dataview], { type });

        // eslint-disable-next-line no-restricted-globals
        self.postMessage({ command: 'exportWAV', data: audioBlob });
      }

      function getBuffer() {
        const buffers = [];
        for (let channel = 0; channel < numChannels; channel++) {
          buffers.push(mergeBuffers(recBuffers[channel], recLength));
        }
          // eslint-disable-next-line no-restricted-globals
        self.postMessage({ command: 'getBuffer', data: buffers });
      }

      function clear() {
        recLength = 0;
        recBuffers = [];
        initBuffers();
      }

      function initBuffers() {
        recBuffers = [];
        for (let channel = 0; channel < numChannels; channel++) {
          recBuffers[channel] = [];
        }
      }

      function mergeBuffers(recBuffers, recLength) {
        const result = new Float32Array(recLength);
        let offset = 0;
        for (let i = 0; i < recBuffers.length; i++) {
          result.set(recBuffers[i], offset);
          offset += recBuffers[i].length;
        }
        return result;
      }

      function interleave(inputL, inputR) {
        const length = inputL.length + inputR.length;
        const result = new Float32Array(length);

        let index = 0;
        let inputIndex = 0;

        while (index < length) {
          result[index++] = inputL[inputIndex];
          result[index++] = inputR[inputIndex];
          inputIndex++;
        }
        return result;
      }

      function floatTo16BitPCM(output, offset, input) {
        for (let i = 0; i < input.length; i++, offset += 2) {
          const s = Math.max(-1, Math.min(1, input[i]));
          output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
      }

      function writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      }

      function encodeWAV(samples) {
        const buffer = new ArrayBuffer(44 + samples.length * 2);
        const view = new DataView(buffer);

        writeString(view, 0, 'RIFF');
        view.setUint32(4, 36 + samples.length * 2, true);
        writeString(view, 8, 'WAVE');
        writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * 4, true);
        view.setUint16(32, numChannels * 2, true);
        view.setUint16(34, 16, true);
        writeString(view, 36, 'data');
        view.setUint32(40, samples.length * 2, true);

        floatTo16BitPCM(view, 44, samples);

        return view;
      }
      // eslint-disable-next-line no-restricted-globals
    }, self);

    this.worker.postMessage({
      command: 'init',
      config: {
        sampleRate: this.context.sampleRate,
        numChannels: this.config.numChannels,
      },
    });

    this.worker.onmessage = (e) => {
      const cb = this.callbacks[e.data.command].pop();
      if (typeof cb === 'function') {
        cb(e.data.data);
      }
    };
  }

  record() {
    this.recording = true;
  }

  stop() {
    this.recording = false;
  }

  clear() {
    this.worker.postMessage({ command: 'clear' });
  }

  getBuffer(cb) {
    cb = cb || this.config.callback;
    if (!cb) throw new Error('Callback not set');

    this.callbacks.getBuffer.push(cb);
    this.worker.postMessage({ command: 'getBuffer' });
  }

  exportWAV(cb, mimeType) {
    mimeType = mimeType || this.config.mimeType;
    cb = cb || this.config.callback;
    if (!cb) throw new Error('Callback not set');

    this.callbacks.exportWAV.push(cb);
    this.worker.postMessage({
      command: 'exportWAV',
      type: mimeType,
    });
  }

  static forceDownload(blob, filename) {
    const url = (window.URL || window.webkitURL).createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename || 'output.wav';
    const click = document.createEvent('Event');
    click.initEvent('click', true, true);
    link.dispatchEvent(click);
  }
}

export default Recorder;
